import { OrderSide } from '@tassat/common-definitions';
import { PriceRecord } from '@tassat/fred-definitions';
import { SecurityCurrencyAssetPair } from '@tassat/fred-extended-definitions';
import { convertToNumber, formatAsAssetNoSign } from '@tassat/utility';

import { IFormErrors, ITransactFormValues } from './types';
import {
    validateDescriptionFields,
    validateMultiCurrency,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable
} from '../helpers/validationHelpers';

export const validateTransactForm = (
    values: ITransactFormValues,
    opts: {
        accountPositionCcyBalance?: {
            availableBalance: number;
            currentBalance: number;
        };
        accountPositionSecurityBalance?: {
            availableBalance: number;
            currentBalance: number;
        };
        realTimePrice?: PriceRecord;
        assetPair?: SecurityCurrencyAssetPair;
    }
) => {
    const {
        accountPositionSecurityBalance,
        accountPositionCcyBalance,
        realTimePrice,
        assetPair
    } = opts;

    const errors: IFormErrors = {} as IFormErrors;

    if (!values.AssetType.value) {
        errors.AssetType = 'Please select an asset type';
    }

    const decimalCheck = (values.Type === OrderSide.BUY || values.Type === OrderSide.SUBSCRIPTION) ?
        2
        : 6;

    const amountError = validateMultiCurrency(values.Amount, decimalCheck);
    const amountAsNumber = convertToNumber(values.Amount ?? 0);
    if (amountError) {
        errors.Amount = amountError;
    } else if (
        amountAsNumber && values.Type === OrderSide.SUBSCRIPTION &&
        amountAsNumber > (accountPositionCcyBalance?.availableBalance || 0)
    ) {
        errors.Amount = `Amount must not exceed the available ${assetPair?.currency?.Ticker} balance of the account`;
    } else if (
        amountAsNumber && values.Type === OrderSide.REDEEM &&
        amountAsNumber > (accountPositionSecurityBalance?.availableBalance || 0)
    ) {
        errors.Amount = `Amount must not exceed the available ${assetPair?.security?.Ticker} balance of the account`;
    } else if (
        amountAsNumber && values.Type === OrderSide.BUY &&
        accountPositionCcyBalance?.availableBalance !== undefined
    ) {
        const totalAmount = amountAsNumber + convertToNumber(values.Fee);

        if (totalAmount > (accountPositionCcyBalance?.availableBalance || 0)) {
            errors.Amount = `Amount must not exceed the available ${assetPair?.currency?.Ticker} balance of the account`;
        }
    } else if (
        amountAsNumber && values.Type === OrderSide.SELL &&
        accountPositionSecurityBalance?.availableBalance !== undefined && realTimePrice
    ) {
        if (amountAsNumber > (accountPositionSecurityBalance.availableBalance || 0)) {
            errors.Amount = `Amount must not exceed the available ${assetPair?.security?.Ticker} balance of the account`;
        }

        const amountWithFee =
            amountAsNumber * convertToNumber(realTimePrice.Price) - convertToNumber(formatAsAssetNoSign(values.Fee, assetPair?.currency));
        if (amountWithFee < 0) {
            errors.Amount = 'Invalid amount entered';
        }
    }

    const privateDescription = values.PrivateDescription.trim();

    const privateDescriptionError = validateDescriptionFields(privateDescription, 'Private Description');
    const privateDescriptionExcelError = validateNoExcelFormulaStartingCharacters(privateDescription);
    const privateDescriptionNonPrintableError = validateNoNonPrintable(privateDescription);

    if (privateDescriptionError) {
        errors.PrivateDescription = privateDescriptionError;
    } else if (privateDescriptionExcelError) {
        errors.PrivateDescription = privateDescriptionExcelError;
    } else if (privateDescriptionNonPrintableError) {
        errors.PrivateDescription = privateDescriptionNonPrintableError;
    }

    return errors;
};